import { useEffect, useRef, useState } from "react";
import { Search, MapPin, Zap, Building2, AlertTriangle, Phone, Copy, Check, ArrowRight, Home as HomeIcon } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import Seo from "@/components/Seo";
import NewsletterSignupBlock from "@/components/NewsletterSignupBlock";
import JsonLd from "@/components/JsonLd";
import { buildEsidLookupJsonLd } from "@/lib/jsonLd";
import { statePath } from "@/lib/stateRegistry";
import { DIRECTORY, cardId } from "@/lib/utilitiesDirectory";
import { api } from "../lib/api";
import { toast } from "sonner";

// Real, ERCOT-backed lookup (Technical Blueprint Section 26) -- the server answers one of three
// honest shapes, never a guess: "matched" (exactly one confident ESID), "not_found" (zero matches;
// offers self-select as a fallback, the same pattern already used for bill analysis's utility
// confirmation), or "multiple" (more than one meter at this street address -- the customer confirms
// their own unit rather than the system silently picking one).
// The lookup API's TDU ids -> utility directory codes. The outage number shown below comes ONLY from the verified utility
// directory (content/utilities/utilities.json), never from the API response. AEP Texas Central and North share their numbers.
const API_TDU_TO_DIRECTORY_CODE = { oncor: "oncor", centerpoint: "centerpoint", "aep-texas": "aep_texas_central", tnmp: "tnmp" };

function directoryUtilityFor(tdu) {
  const code = tdu && (API_TDU_TO_DIRECTORY_CODE[tdu.id] || tdu.id);
  return (code && DIRECTORY.byCode[code]) || null;
}

export default function EsidLookup() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Prefilled from the homepage's lightweight lookup form (?address=&zip=), which hands off to
  // this page for the actual result. Direct visits (no params) start blank as before.
  const [address, setAddress] = useState(() => searchParams.get("address") || "");
  const [zip, setZip] = useState(() => (searchParams.get("zip") || "").replace(/\D/g, "").slice(0, 5));
  // Owner-reported bug (2026-09-28): re-visiting this exact URL later -- a reopened/pinned tab, the
  // browser's own address-bar suggestion of a previously-visited URL, a stale bookmark, or Back/
  // Forward -- silently re-surfaced an old test address ("4922...") as if it were a hardcoded
  // default. Root cause confirmed live: it was never the AddressAutocomplete component (its state
  // is always "" on mount, and its session-only cache can't survive a page reload) and never
  // browser-native autofill (this field has no name/id for Chrome to key a saved value against, and
  // a real type+submit+relaunch test against a fresh Chrome profile wrote zero rows to Chrome's
  // autofill tables). It was this page's own intentional query-param prefill above, working exactly
  // as designed -- the bug is that the URL keeps carrying ?address=&zip= after they've been consumed,
  // so any later, unrelated visit to that same URL replays the old values. Scrub the params from the
  // address bar (and history entry) right after they've been read into state, once, on mount --
  // this doesn't change the one-time homepage-handoff behavior itself, it only stops that handoff's
  // URL from being replayable later.
  useEffect(() => {
    if (searchParams.get("address") || searchParams.get("zip")) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [result, setResult] = useState(null);
  const [selectedUtility, setSelectedUtility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const autoSubmitted = useRef(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    setResult(null);
    setSelectedUtility(null);
    if (!address.trim() || !/^\d{5}$/.test(zip)) {
      setError("Enter a full street address and a 5-digit ZIP code.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/esid/lookup", { address, zip_code: zip });
      setResult(r.data);
    } catch (err) {
      // detail can be a string, an object or a list, and only a string is ever shown: React cannot render an object.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const unavailable = status === 503 || (detail && typeof detail === "object" && !Array.isArray(detail)
        && detail.code === "ESID_REGISTRY_NOT_READY");
      setError(unavailable
        ? "The ESID lookup is temporarily unavailable. Nothing was looked up -- please try again shortly."
        : typeof detail === "string" && detail.trim() ? detail : "Unable to look up this address.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-run the lookup once when arriving with both params pre-filled (the homepage handoff);
  // a direct visit with no params, or with only one, leaves the form for the visitor to submit.
  useEffect(() => {
    if (autoSubmitted.current) return;
    autoSubmitted.current = true;
    if (address.trim() && /^\d{5}$/.test(zip)) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyEsid = (value) => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success("ESID copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const matched = result?.status === "matched" ? result : null;
  const chosenCandidate = result?.status === "multiple" && selectedUtility ? selectedUtility : null;
  const display = matched || chosenCandidate;

  return (
    <div data-testid="esid-page">
      <Seo title="Find your Texas ESID" description="Look up the Electric Service Identifier (ESID) for a Texas service address." path={statePath("texas", "esid-lookup")} />
      <JsonLd data={buildEsidLookupJsonLd()} />
      <section className="bg-[#F7F9FC] border-b border-slate-100">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
          <span className="pill mb-5"><Zap className="w-3.5 h-3.5" /> Texas utility tool</span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#0A2540]">
            Find your ESID.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-[60ch]">
            Every Texas electric meter has a unique 17- or 22-digit Electric Service Identifier (ESID). Providers ask for it when you start or change service. We look it up using ERCOT&rsquo;s public registry data.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-12 gap-y-10 md:gap-x-10">
          <div className="col-span-12 md:col-span-7">
            <form onSubmit={submit} className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm" data-testid="esid-form">
              {/* A <div> + htmlFor (not a wrapping <label>): a wrapping label would fold the whole
                  suggestion popover into the input's accessible name. */}
              <div className="block mb-5">
                <label htmlFor="esid-address-input" className="text-overline text-slate-500 block mb-2">Street address</label>
                <AddressAutocomplete
                  id="esid-address-input"
                  value={address}
                  onChange={setAddress}
                  onSelectSuggestion={(s) => { if (s.zip) setZip(s.zip); }}
                  placeholder="1234 Main St, Apt 5"
                  inputTestId="esid-address"
                  listTestId="esid-address-suggestions"
                  required
                />
              </div>
              <label className="block mb-6">
                <span className="text-overline text-slate-500 block mb-2">ZIP code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="75001"
                  className="input-brutal max-w-[200px]"
                  data-testid="esid-zip"
                  required
                />
              </label>
              <button type="submit" className="btn-green w-full" disabled={loading} data-testid="esid-submit">
                {loading ? "Looking up…" : (<>Look up ESID <Search className="w-4 h-4" /></>)}
              </button>
              {error && (
                <div className="mt-4 flex gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="esid-error">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </form>

            {/* Result region is announced to screen reader users when a lookup completes --
                the panel that appears here is the only signal of a status change once the form
                submit button's "Looking up…" label reverts. */}
            <div aria-live="polite">
              {result?.status === "not_found" && (
                <NotFoundPanel result={result} selected={selectedUtility} onSelect={setSelectedUtility} />
              )}

              {result?.status === "multiple" && !chosenCandidate && (
                <MultiplePanel result={result} onPick={setSelectedUtility} />
              )}

              {display && (
                <MatchCard data={display} zip={zip} onCopy={copyEsid} copied={copied} isRealMatch={!!display.esid} />
              )}
            </div>
          </div>

          <aside className="col-span-12 md:col-span-5">
            <div className="bg-white border border-slate-100 rounded-3xl p-7 sticky top-24">
              <h3 className="font-display text-xl font-bold text-[#0A2540] mb-4">What is an ESID?</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                An <strong>Electric Service Identifier</strong> (sometimes called ESI ID) is a unique 17- or 22-digit number assigned to every electric meter in the deregulated Texas market.
              </p>
              <ul className="space-y-3 text-sm text-slate-700">
                <li className="flex gap-2.5">
                  <MapPin className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" />
                  <span>Tied to your physical address and utility (TDU), not to you personally.</span>
                </li>
                <li className="flex gap-2.5">
                  <Building2 className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" />
                  <span>Your ESID stays the same when you change retail electric providers.</span>
                </li>
                <li className="flex gap-2.5">
                  <Zap className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" />
                  <span>Used to identify your meter when service starts or changes.</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-slate-100 text-xs text-slate-500">
                Data sourced from ERCOT&rsquo;s public TDSP ESI ID registry. Available only for Texas addresses in the ERCOT service area.
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Flag-gated. Below the lookup and its result (matched, several meters, or utility self-selected), never above them. The
          utility is the lookup's own TDU when there is one; it only pre-selects the newsletter's optional utility field. */}
      <NewsletterSignupBlock placement="esid" utility={(display || selectedUtility)?.tdu_code || null} />
    </div>
  );
}

/** Zero matches: the honest "couldn't confirm" state, never a guess. Offers the same self-select
 * fallback pattern already used for bill analysis's utility confirmation (BillCheck.jsx). */
function NotFoundPanel({ result, selected, onSelect }) {
  return (
    <div className="mt-6 bg-amber-50 border border-amber-200 rounded-3xl p-7" data-testid="esid-not-found">
      <p className="text-sm text-amber-900">{result.message}</p>
      {!selected && result.tdu_options?.length > 0 && (
        <div className="mt-4">
          <label htmlFor="esid-utility-select" className="text-overline text-amber-800 block mb-2">
            Select your utility instead
          </label>
          <select
            id="esid-utility-select"
            className="input-brutal max-w-[320px]"
            defaultValue=""
            data-testid="esid-utility-select"
            onChange={(e) => {
              const opt = result.tdu_options.find((o) => o.code === e.target.value);
              if (opt) onSelect({ tdu_code: opt.code, tdu_name: opt.name, esid: null, address: null });
            }}
          >
            <option value="" disabled>Choose your utility</option>
            {result.tdu_options.map((o) => (
              <option key={o.code} value={o.code}>{o.name}</option>
            ))}
          </select>
        </div>
      )}
      {selected && (
        <p className="mt-4 text-sm text-amber-900" data-testid="esid-utility-selected">
          Utility on file: <strong>{selected.tdu_name}</strong>. Your ESID itself is printed on your electric bill.
        </p>
      )}
    </div>
  );
}

/** More than one meter at this street address (an apartment/multi-unit building): the customer
 * confirms which unit is theirs -- this is a real fact the customer knows about their own home,
 * not the system guessing on their behalf. */
function MultiplePanel({ result, onPick }) {
  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-3xl p-7" data-testid="esid-multiple">
      <p className="text-sm text-slate-700 mb-4">{result.message}</p>
      <ul className="space-y-2">
        {result.candidates.map((c) => (
          <li key={c.esid}>
            <button
              type="button"
              onClick={() => onPick(c)}
              className="w-full text-left px-4 py-3 rounded-xl border border-slate-200 hover:border-[#16A34A] hover:bg-emerald-50 transition-colors flex items-center justify-between"
              data-testid="esid-candidate"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
                <HomeIcon className="w-4 h-4 text-slate-400" />
                {c.unit || c.address}
              </span>
              <span className="text-xs text-slate-500">{c.tdu_name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MatchCard({ data, zip, onCopy, copied, isRealMatch }) {
  return (
    <div className="mt-6 bg-[#0A2540] text-white rounded-3xl p-8 relative overflow-hidden" data-testid="esid-result">
      <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#16A34A] opacity-20 rounded-full blur-3xl" />
      {isRealMatch ? (
        <>
          <p className="text-overline text-[#16A34A] mb-3 relative">Your ESID</p>
          <div className="flex items-center gap-3 relative">
            <p className="font-mono text-2xl md:text-3xl font-bold tabular-nums tracking-wide break-all">{data.esid}</p>
            <button
              onClick={() => onCopy(data.esid)}
              className="shrink-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="esid-copy"
              aria-label={copied ? "ESID copied to clipboard" : "Copy ESID to clipboard"}
              title="Copy to clipboard"
            >
              {copied ? <Check className="w-4 h-4 text-[#16A34A]" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
            </button>
          </div>
          <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 gap-4 text-sm relative">
            <div>
              <p className="text-xs text-slate-400 mb-1">Serving utility (TDU)</p>
              <p className="font-semibold flex items-center gap-1.5">
                {data.tdu?.color && <span className="w-2.5 h-2.5 rounded-full" style={{ background: data.tdu.color }} />}
                {data.tdu?.name || data.tdu_name}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Region</p>
              <p className="font-semibold">{data.tdu?.region || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Premise type</p>
              <p className="font-semibold">{data.premise_type || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Status</p>
              <p className="font-semibold text-[#16A34A]">{data.service_status || "—"}</p>
            </div>
          </div>
          {(() => {
            const utility = directoryUtilityFor(data.tdu);
            const outage = utility && utility.phones.find((p) => p.kind === "outage");
            if (!utility || !outage) return null;
            return (
              <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm relative" data-testid="esid-outage-line">
                <Phone className="w-4 h-4 text-[#4ADE80]" aria-hidden="true" />
                <span className="text-slate-300">Outage line:</span>
                <a href={outage.href} aria-label={`Call ${utility.name} outage line: ${outage.number}`} className="font-mono font-semibold text-white underline">{outage.number}</a>
                <Link to={`${statePath("texas", "utilities")}#${cardId(utility.code)}`} className="text-[#4ADE80] underline" aria-label={`All ${utility.name} phone numbers and FAQs`}>All phone numbers</Link>
              </div>
            );
          })()}
          <Link to={`${statePath("texas", "plans")}?zip=${data.zip_code}`} className="btn-green mt-7 w-full md:w-auto relative" data-testid="esid-find-plans">
            Get notified about plans for this address <ArrowRight className="w-4 h-4" />
          </Link>
        </>
      ) : (
        <>
          <p className="text-overline text-[#16A34A] mb-3 relative">Your utility</p>
          <p className="text-2xl font-bold relative">{data.tdu_name}</p>
          <p className="mt-3 text-sm text-slate-300 relative">Your ESID is printed on your electric bill &mdash; look for &ldquo;ESI ID.&rdquo;</p>
          <Link to={`${statePath("texas", "plans")}?zip=${zip}`} className="btn-green mt-7 w-full md:w-auto relative" data-testid="esid-find-plans">
            Get notified about plans for this address <ArrowRight className="w-4 h-4" />
          </Link>
        </>
      )}
    </div>
  );
}
